
import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useApp } from '../context/AppContext';
import { Navigate, useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft, Save, Sparkles, AlertCircle, Wand2, Loader2, 
  BrainCircuit, Type, FileText, Zap, Image as ImageIcon, Search,
  RefreshCcw, CheckCircle2, Upload, X
} from 'lucide-react';
import { GoogleGenAI } from "@google/genai";

declare var process: any;

const CATEGORIES = ['Community', 'Indian Compliance', 'UAE Compliance', 'Accounting', 'Process'] as const;

const CreatePost: React.FC = () => {
  const { currentUser, addPost } = useApp();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [title, setTitle] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [metaTitle, setMetaTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [category, setCategory] = useState<typeof CATEGORIES[number]>('Community');
  const [excerpt, setExcerpt] = useState('');
  const [content, setContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAiLoading, setIsAiLoading] = useState(false);
  const [isVisualLoading, setIsVisualLoading] = useState(false);

  if (!currentUser) return <Navigate to="/blog" replace />;

  const callAiText = async (prompt: string) => {
    setIsAiLoading(true);
    try {
      const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
      const response = await ai.models.generateContent({
        model: 'gemini-3-flash-preview',
        contents: prompt,
        config: {
          systemInstruction: "You are an expert financial content architect. Return authoritative, polished text.",
        },
      });
      return response.text;
    } catch (err) {
      console.error("AI Assistant Error:", err);
      return null;
    } finally {
      setIsAiLoading(false);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImageUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const suggestAiVisual = async () => {
    if (!title && !excerpt) {
      alert("Please provide a title or excerpt for context.");
      return;
    }
    setIsVisualLoading(true);
    try {
      const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
      const response = await ai.models.generateContent({
        model: 'gemini-2.5-flash-image',
        contents: {
          parts: [{ text: `A premium, clean, professional SaaS technical illustration for a blog titled: ${title}. Isometric dashboard, financial charts, dark blue and slate gray palette, minimalist 3D design, high resolution.` }]
        },
        config: {
          imageConfig: { aspectRatio: "16:9" }
        }
      });

      const parts = response.candidates?.[0]?.content?.parts;
      if (parts) {
        for (const part of parts) {
          if (part.inlineData) {
            setImageUrl(`data:image/png;base64,${part.inlineData.data}`);
            break;
          }
        }
      }
    } catch (err) {
      console.error("Visual Engine Error:", err);
      alert("AI Image generation failed. Using default.");
    } finally {
      setIsVisualLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title || !content || !excerpt) return;
    setIsSubmitting(true);
    setTimeout(() => {
      addPost({ 
        title, 
        imageUrl: imageUrl || 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?q=80&w=2000&auto=format&fit=crop',
        metaTitle, 
        metaDescription, 
        category, 
        excerpt, 
        content, 
        readingTime: `${Math.max(1, Math.round(content.split(' ').length / 200))} min read` 
      });
      navigate('/blog');
    }, 1000);
  };

  return (
    <div className="pt-32 pb-24 bg-slate-50 min-h-screen text-slate-900">
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-8">
          <Link to="/profile" className="inline-flex items-center gap-2 text-slate-500 hover:text-indigo-600 font-semibold mb-12 transition-colors">
            <ArrowLeft size={18} /> Back to Identity Architect
          </Link>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white border border-slate-200 rounded-[3rem] p-8 md:p-14 shadow-2xl relative overflow-hidden">
            <h1 className="text-4xl font-display font-black text-slate-900 mb-10">Construct Intelligence</h1>
            
            <form onSubmit={handleSubmit} className="space-y-8">
              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-1">Headline</label>
                <input 
                  type="text" required value={title} 
                  onChange={(e) => setTitle(e.target.value)} 
                  placeholder="Mastering the Regulatory Corridor..." 
                  className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-6 py-4 text-slate-900 text-xl focus:border-indigo-500 outline-none placeholder:text-slate-400 focus:bg-white" 
                />
              </div>

              <div className="space-y-4">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-1">Featured Visual</label>
                <div className="relative aspect-video rounded-3xl bg-slate-50 border border-slate-200 overflow-hidden group">
                  {imageUrl ? (
                    <img src={imageUrl} className="w-full h-full object-cover" alt="Featured" />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-slate-500">
                      {isVisualLoading ? <Loader2 className="animate-spin" size={48} /> : <ImageIcon size={48} />}
                      <p className="mt-4 font-bold text-sm">No Image Selected</p>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-slate-900/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-4">
                    <button type="button" onClick={suggestAiVisual} className="px-5 py-3 bg-indigo-600 rounded-xl text-white font-bold flex items-center gap-2 shadow-lg hover:bg-indigo-500">
                      <Sparkles size={16} /> Suggest AI Visual
                    </button>
                    <button type="button" onClick={() => fileInputRef.current?.click()} className="px-5 py-3 bg-white/10 backdrop-blur-md rounded-xl text-white font-bold flex items-center gap-2 border border-white/20 hover:bg-white/20">
                      <Upload size={16} /> Upload Local
                    </button>
                  </div>
                  <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleFileUpload} />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-1">Summary Excerpt</label>
                <textarea 
                  required value={excerpt} 
                  onChange={(e) => setExcerpt(e.target.value)} 
                  placeholder="A strategic executive summary for the insight..." 
                  className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-6 py-4 text-slate-600 focus:border-indigo-500 outline-none min-h-[100px] placeholder:text-slate-400 focus:bg-white" 
                />
              </div>

              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-1">Analysis Content</label>
                <textarea 
                  required value={content} 
                  onChange={(e) => setContent(e.target.value)} 
                  placeholder="Deploy full technical analysis here..." 
                  className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-6 py-4 text-slate-900 focus:border-indigo-500 outline-none min-h-[400px] font-sans text-lg leading-relaxed placeholder:text-slate-400 focus:bg-white" 
                />
              </div>

              <button type="submit" disabled={isSubmitting} className="w-full py-5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-2xl shadow-xl transition-all flex items-center justify-center gap-3">
                {isSubmitting ? 'Deploying...' : <><Save size={20} /> Publish Institutional Insight</>}
              </button>
            </form>
          </motion.div>
        </div>

        <aside className="lg:col-span-4">
          <div className="sticky top-32 space-y-6">
            <div className="p-8 rounded-[2.5rem] bg-white border border-indigo-100 shadow-xl shadow-indigo-100/50">
              <h3 className="text-xl font-display font-bold text-slate-900 mb-6 flex items-center gap-2">
                <BrainCircuit className="text-indigo-600" />
                Sovereign Assistant
              </h3>
              <p className="text-slate-500 text-sm mb-6">Let AI assist in refining your institutional discourse.</p>
              <div className="space-y-4">
                <button 
                  onClick={() => callAiText(`Polish this for an institutional audience: ${content}`)}
                  className="w-full flex items-center justify-between p-4 bg-slate-50 border border-slate-200 rounded-2xl hover:border-indigo-200 hover:bg-white transition-all text-xs font-bold text-indigo-600"
                >
                  Enhance Technical Precision <Zap size={14} />
                </button>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default CreatePost;
